export type DialogueBubble = {
  body: string;
};

export type GuideSpeechStep = {
  kind: "guide";
  messages: DialogueBubble[];
};

export type GuideChoiceOption = {
  label: string;
  reply: DialogueBubble[];
};

export type GuideChoiceStep = {
  kind: "choice";
  choices: GuideChoiceOption[];
  after: DialogueBubble[];
};

export type GuideStep = GuideSpeechStep | GuideChoiceStep;

export type PuzzleHints = {
  level_1: string;
  level_2: string;
};

export type PuzzleStep = {
  is_free: boolean;
  question: string;
  correct_answer: string;
  error_reply: DialogueBubble[];
  hints: PuzzleHints;
};

export type DialoguePerson = {
  name: string;
  avatarUrl: string | null;
};

export type SystemDialogue = {
  cast: {
    protagonist: string;
    guide: string;
    player: string;
  };
  guide: DialoguePerson;
  player: DialoguePerson;
  guideSteps: GuideStep[];
  puzzleSteps: PuzzleStep[];
  closing: {
    statement: string;
    truth_review: string;
  } | null;
};

export type ChatLine =
  | { id: string; role: "guide" | "player"; body: string }
  | { id: string; role: "marker"; body: string }
  | { id: string; role: "closing"; statement: string; truthReview: string };

export type DialogueInput =
  | { type: "choice"; choices: { label: string }[] }
  | {
      type: "puzzle";
      index: number;
      total: number;
      question: string;
      hints: PuzzleHints;
      correctAnswer: string;
      isFree: boolean;
      hintLevel: 0 | 1 | 2 | 3;
    }
  | { type: "done" };

export type DialoguePhase = "guide" | "puzzle" | "done";

export type DialogueState = {
  lines: ChatLine[];
  input: DialogueInput;
  phase: DialoguePhase;
  guideIndex: number;
  puzzleIndex: number;
  nextId: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function parseBubbles(value: unknown): DialogueBubble[] {
  if (!Array.isArray(value)) return [];
  const out: DialogueBubble[] = [];
  for (const item of value) {
    if (isRecord(item) && nonEmptyString(item.body)) {
      out.push({ body: item.body.trim() });
    }
  }
  return out;
}

function personFrom(
  people: Map<string, DialoguePerson>,
  name: string,
  fallback: string,
): DialoguePerson {
  const trimmed = name.trim() || fallback;
  return people.get(trimmed) ?? { name: trimmed, avatarUrl: null };
}

function parsePeople(input: Record<string, unknown>): {
  byName: Map<string, DialoguePerson>;
  guideName: string;
  playerName: string;
  protagonistName: string;
} {
  const byName = new Map<string, DialoguePerson>();
  let flaggedGuide = "";
  let flaggedPlayer = "";
  let flaggedProtagonist = "";
  const canon = isRecord(input.canon) ? input.canon : {};
  const people = Array.isArray(canon.people) ? canon.people : [];
  for (const row of people) {
    if (!isRecord(row) || !nonEmptyString(row.name)) continue;
    const name = row.name.trim();
    const avatarUrl = nonEmptyString(row.avatar_url) ? row.avatar_url.trim() : null;
    byName.set(name, { name, avatarUrl });
    if (row.is_guide === true && !flaggedGuide) flaggedGuide = name;
    if (row.is_player === true && !flaggedPlayer) flaggedPlayer = name;
    if (row.is_protagonist === true && !flaggedProtagonist) {
      flaggedProtagonist = name;
    }
  }
  const game = isRecord(input.game) ? input.game : {};
  const cast = isRecord(game.cast) ? game.cast : {};
  const guideName = nonEmptyString(cast.guide)
    ? cast.guide.trim()
    : flaggedGuide || "引导人";
  const playerName = nonEmptyString(cast.player)
    ? cast.player.trim()
    : flaggedPlayer || "玩家";
  const protagonistName = nonEmptyString(cast.protagonist)
    ? cast.protagonist.trim()
    : flaggedProtagonist || "机主";
  return { byName, guideName, playerName, protagonistName };
}

function parseGuideStep(value: unknown): GuideStep | null {
  if (!isRecord(value)) return null;
  if (value.kind === "guide") {
    const messages = parseBubbles(value.messages);
    if (messages.length === 0) return null;
    return { kind: "guide", messages };
  }
  if (value.kind === "choice") {
    const raw = Array.isArray(value.choices) ? value.choices : [];
    const choices: GuideChoiceOption[] = [];
    for (const item of raw) {
      if (!isRecord(item) || !nonEmptyString(item.label)) continue;
      choices.push({
        label: item.label.trim(),
        reply: parseBubbles(item.reply),
      });
    }
    if (choices.length === 0) return null;
    return {
      kind: "choice",
      choices,
      after: parseBubbles(value.after),
    };
  }
  return null;
}

function parseHints(value: unknown): PuzzleHints {
  if (!isRecord(value)) return { level_1: "", level_2: "" };
  return {
    level_1: nonEmptyString(value.level_1) ? value.level_1.trim() : "",
    level_2: nonEmptyString(value.level_2) ? value.level_2.trim() : "",
  };
}

function parsePuzzleStep(value: unknown): PuzzleStep | null {
  if (!isRecord(value) || !nonEmptyString(value.question)) return null;
  return {
    is_free: value.is_free === true,
    question: value.question.trim(),
    correct_answer: nonEmptyString(value.correct_answer)
      ? value.correct_answer.trim()
      : "",
    error_reply: parseBubbles(value.error_reply),
    hints: parseHints(value.hints),
  };
}

export function parseSystemDialogue(input: unknown): SystemDialogue | null {
  if (!isRecord(input)) return null;
  const { byName, guideName, playerName, protagonistName } = parsePeople(input);
  const game = isRecord(input.game) ? input.game : {};
  const guideSteps: GuideStep[] = [];
  if (Array.isArray(game.guideSteps)) {
    for (const step of game.guideSteps) {
      const parsed = parseGuideStep(step);
      if (parsed) guideSteps.push(parsed);
    }
  }
  const puzzleSteps: PuzzleStep[] = [];
  if (Array.isArray(game.puzzleSteps)) {
    for (const step of game.puzzleSteps) {
      const parsed = parsePuzzleStep(step);
      if (parsed) puzzleSteps.push(parsed);
    }
  }
  const closingRaw = isRecord(game.closing) ? game.closing : null;
  const closing =
    closingRaw &&
    nonEmptyString(closingRaw.statement) &&
    nonEmptyString(closingRaw.truth_review)
      ? {
          statement: closingRaw.statement.trim(),
          truth_review: closingRaw.truth_review.trim(),
        }
      : null;
  return {
    cast: {
      protagonist: protagonistName,
      guide: guideName,
      player: playerName,
    },
    guide: personFrom(byName, guideName, "引导人"),
    player: personFrom(byName, playerName, "玩家"),
    guideSteps,
    puzzleSteps,
    closing,
  };
}

export function hasPlayableDialogue(
  dialogue: SystemDialogue | null,
): dialogue is SystemDialogue {
  return Boolean(
    dialogue &&
      (dialogue.guideSteps.length > 0 || dialogue.puzzleSteps.length > 0),
  );
}

export function normalizeAnswer(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

export function answersMatch(input: string, reference: string): boolean {
  const a = normalizeAnswer(input);
  const b = normalizeAnswer(reference);
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
}

function emptyState(): DialogueState {
  return {
    lines: [],
    input: { type: "done" },
    phase: "done",
    guideIndex: 0,
    puzzleIndex: 0,
    nextId: 1,
  };
}

function pushLine(
  state: DialogueState,
  line: Omit<Extract<ChatLine, { role: "guide" | "player" | "marker" }>, "id">,
): DialogueState {
  const id = `line-${state.nextId}`;
  return {
    ...state,
    nextId: state.nextId + 1,
    lines: [...state.lines, { ...line, id }],
  };
}

function pushClosing(
  state: DialogueState,
  statement: string,
  truthReview: string,
): DialogueState {
  const id = `line-${state.nextId}`;
  return {
    ...state,
    nextId: state.nextId + 1,
    lines: [...state.lines, { id, role: "closing", statement, truthReview }],
  };
}

function pushBubbles(
  state: DialogueState,
  role: "guide" | "player",
  bubbles: DialogueBubble[],
): DialogueState {
  let next = state;
  for (const bubble of bubbles) {
    next = pushLine(next, { role, body: bubble.body });
  }
  return next;
}

function finishClosing(
  state: DialogueState,
  dialogue: SystemDialogue,
): DialogueState {
  let next = pushLine(state, { role: "marker", body: "结案" });
  if (dialogue.closing) {
    next = pushClosing(
      next,
      dialogue.closing.statement,
      dialogue.closing.truth_review,
    );
  }
  return {
    ...next,
    phase: "done",
    input: { type: "done" },
  };
}

function openPuzzle(
  state: DialogueState,
  dialogue: SystemDialogue,
  index: number,
): DialogueState {
  const step = dialogue.puzzleSteps[index];
  if (!step) return finishClosing(state, dialogue);
  let next = pushLine(state, {
    role: "marker",
    body: `任务 ${index + 1}/${dialogue.puzzleSteps.length}`,
  });
  next = pushLine(next, { role: "guide", body: step.question });
  return {
    ...next,
    phase: "puzzle",
    puzzleIndex: index,
    input: {
      type: "puzzle",
      index,
      total: dialogue.puzzleSteps.length,
      question: step.question,
      hints: step.hints,
      correctAnswer: step.correct_answer,
      isFree: step.is_free,
      hintLevel: 0,
    },
  };
}

function enterPuzzles(
  state: DialogueState,
  dialogue: SystemDialogue,
): DialogueState {
  const next = pushLine(state, { role: "marker", body: "进入手机" });
  if (dialogue.puzzleSteps.length === 0) {
    return finishClosing(next, dialogue);
  }
  return openPuzzle(next, dialogue, 0);
}

function playGuideUntilPrompt(
  state: DialogueState,
  dialogue: SystemDialogue,
): DialogueState {
  let next = state;
  while (next.guideIndex < dialogue.guideSteps.length) {
    const step = dialogue.guideSteps[next.guideIndex];
    if (!step) break;
    if (step.kind === "guide") {
      next = pushBubbles(next, "guide", step.messages);
      next = { ...next, guideIndex: next.guideIndex + 1 };
      continue;
    }
    return {
      ...next,
      phase: "guide",
      input: {
        type: "choice",
        choices: step.choices.map((choice) => ({ label: choice.label })),
      },
    };
  }
  return enterPuzzles(next, dialogue);
}

export function startDialogue(dialogue: SystemDialogue): DialogueState {
  return playGuideUntilPrompt(
    {
      ...emptyState(),
      phase: "guide",
      input: { type: "done" },
    },
    dialogue,
  );
}

export function skipToPuzzles(dialogue: SystemDialogue): DialogueState {
  return enterPuzzles(
    {
      ...emptyState(),
      phase: "puzzle",
    },
    dialogue,
  );
}

export function pickChoice(
  dialogue: SystemDialogue,
  state: DialogueState,
  choiceIndex: number,
): DialogueState {
  if (state.input.type !== "choice") return state;
  const step = dialogue.guideSteps[state.guideIndex];
  if (!step || step.kind !== "choice") return state;
  const choice = step.choices[choiceIndex];
  if (!choice) return state;
  let next = pushLine(state, { role: "player", body: choice.label });
  next = pushBubbles(next, "guide", choice.reply);
  next = pushBubbles(next, "guide", step.after);
  next = { ...next, guideIndex: next.guideIndex + 1 };
  return playGuideUntilPrompt(next, dialogue);
}

function advanceAfterCorrect(
  dialogue: SystemDialogue,
  state: DialogueState,
): DialogueState {
  const nextIndex = state.puzzleIndex + 1;
  if (nextIndex >= dialogue.puzzleSteps.length) {
    return finishClosing(state, dialogue);
  }
  return openPuzzle(state, dialogue, nextIndex);
}

export function submitPuzzleAnswer(
  dialogue: SystemDialogue,
  state: DialogueState,
  text: string,
): DialogueState {
  if (state.input.type !== "puzzle") return state;
  const answer = text.trim();
  if (!answer) return state;
  const step = dialogue.puzzleSteps[state.puzzleIndex];
  if (!step) return state;
  const next = pushLine(state, { role: "player", body: answer });
  if (answersMatch(answer, step.correct_answer)) {
    return advanceAfterCorrect(dialogue, next);
  }
  return {
    ...pushBubbles(next, "guide", step.error_reply),
    input: { ...state.input, hintLevel: state.input.hintLevel },
  };
}

export function judgePuzzle(
  dialogue: SystemDialogue,
  state: DialogueState,
  correct: boolean,
  typedText?: string,
): DialogueState {
  if (state.input.type !== "puzzle") return state;
  const step = dialogue.puzzleSteps[state.puzzleIndex];
  if (!step) return state;
  const typed = typedText?.trim() ?? "";
  let next = state;
  if (typed) {
    next = pushLine(next, { role: "player", body: typed });
  }
  if (correct) {
    return advanceAfterCorrect(dialogue, next);
  }
  return {
    ...pushBubbles(next, "guide", step.error_reply),
    input: { ...state.input, hintLevel: state.input.hintLevel },
  };
}

export function revealHint(state: DialogueState): DialogueState {
  if (state.input.type !== "puzzle") return state;
  if (state.input.hintLevel >= 3) return state;
  return {
    ...state,
    input: {
      ...state.input,
      hintLevel: (state.input.hintLevel + 1) as 1 | 2 | 3,
    },
  };
}
